/* RAVIN product-page gallery: exact Scroll Deck card transforms in the original
 * sticky three-column PDP; mobile keeps the original Arc Fan swipe animation.
 * No editorial text, no navigation to other pages, no purchase-state mutations. */
(() => {
  if (window.__RavinPDPDualDeckLoaded) return;
  window.__RavinPDPDualDeckLoaded = true;

  const instances = new WeakMap();
  const selector = '[data-ravin-pdp-dual-deck]';
  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function init(root) {
    if (!root || instances.has(root)) return;
    const host = root.closest('[data-ravin-pdp-editorial]');
    const arc = root.querySelector('[data-ravin-3d-arc-deck]');
    const deck = root.querySelector('[data-ravin-pdp-scroll-deck]');
    const stage = deck?.querySelector('[data-ravin-deck-stage]');
    const cards = Array.from(deck?.querySelectorAll('[data-ravin-deck-card]') || []);
    const arcCards = Array.from(arc?.querySelectorAll('[data-ravin-arc-card]') || []);
    const mobileCurrent = arc?.querySelector('[data-ravin-arc-current]');
    const desktopCurrent = deck?.querySelector('[data-ravin-deck-current]');
    if (!host || !stage || !cards.length || !arc || !arcCards.length) return;

    const controller = new AbortController();
    let raf = 0;
    let lastActive = -1;
    let variantOffset = 0;
    let animatedPosition = 0;
    let lastFrameTime = 0;

    const mediaIndex = (collection, mediaId) =>
      mediaId ? collection.findIndex(card => card.dataset.mediaId === String(mediaId)) : -1;

    function scrollPosition() {
      const stickyContainer = host.firstElementChild;
      const topValue = stickyContainer ? Number.parseFloat(window.getComputedStyle(stickyContainer).top) : 0;
      const stickyTop = Number.isFinite(topValue) ? topValue : 0;
      // Do not advance images while scrolling down to reach the product section.
      // Scroll depth begins only once the original three-column PDP is pinned.
      const depth = Math.max(0, stickyTop - host.getBoundingClientRect().top);
      const travel = Math.max(1, host.offsetHeight - window.innerHeight);
      const firstImageHold = Math.min(window.innerHeight * 0.80, travel * 0.50);
      const perImageTravel = Math.max(1, (travel - firstImageHold) / Math.max(1, cards.length - 1));
      return clamp((depth - firstImageHold) / perImageTravel, 0, cards.length - 1);
    }

    function setCard(card, y, z, tilt, yaw, scale, alpha, layer) {
      const style = card.style;
      style.setProperty('--ravin-deck-y', y.toFixed(2) + 'px');
      style.setProperty('--ravin-deck-z', z.toFixed(2) + 'px');
      style.setProperty('--ravin-deck-tilt', tilt.toFixed(2) + 'deg');
      style.setProperty('--ravin-deck-yaw', yaw.toFixed(2) + 'deg');
      style.setProperty('--ravin-deck-scale', scale.toFixed(4));
      style.setProperty('--ravin-deck-alpha', alpha.toFixed(3));
      style.zIndex = String(layer);
    }

    // Same lift, rotation, perspective, scaling and depth equations as RAVIN 3D Scroll Deck.
    function renderDesktop(now) {
      raf = 0;
      if (!desktop.matches || !deck.isConnected) return;
      const targetPosition = clamp(scrollPosition() + variantOffset, 0, cards.length - 1);
      const elapsed = lastFrameTime ? Math.min(64, Math.max(1, now - lastFrameTime)) : 16;
      lastFrameTime = now;
      if (reducedMotion.matches) {
        animatedPosition = targetPosition;
      } else {
        // Ease towards the scroll position: rapid wheel gestures cannot flash past images.
        const damping = 1 - Math.exp(-elapsed / 190);
        animatedPosition += (targetPosition - animatedPosition) * damping;
        if (Math.abs(targetPosition - animatedPosition) < 0.002) animatedPosition = targetPosition;
      }
      const position = animatedPosition;
      const active = clamp(Math.round(position), 0, cards.length - 1);
      const height = stage.clientHeight;
      if (active !== lastActive) {
        if (desktopCurrent) desktopCurrent.textContent = String(active + 1).padStart(2, '0');
        cards.forEach((card, index) => {
          const focused = index === active;
          card.classList.toggle('is-active', focused);
          card.toggleAttribute('inert', !focused);
          if (focused) card.removeAttribute('aria-hidden');
          else card.setAttribute('aria-hidden', 'true');
        });
        host.currentMediaIndex = active;
        lastActive = active;
      }
      cards.forEach((card, index) => {
        const delta = index - position;
        let y, z, tilt, yaw, scale, alpha;
        if (reducedMotion.matches) {
          y = 0;
          z = 0;
          tilt = 0;
          yaw = 0;
          scale = 1;
          alpha = index === active ? 1 : 0;
        } else if (delta >= 0) {
          const d = Math.min(delta, 5);
          y = -height * (0.11 * d + 0.018 * d * d);
          z = 145 - 190 * d;
          tilt = 73 * Math.min(d, 1) + 3 * Math.max(0, d - 1);
          yaw = Math.sin(index * 1.9) * Math.min(d, 2) * 2.4;
          scale = 1 - Math.min(0.33, 0.075 * d);
          alpha = clamp(1 - Math.max(0, d - 1.3) * 0.32, 0, 1);
        } else {
          const d = -delta;
          y = height * (0.92 * Math.min(d, 1) + 0.5 * Math.max(0, d - 1));
          z = 145 - 245 * Math.min(d, 1) - 80 * Math.max(0, d - 1);
          tilt = -88 * Math.min(d, 1);
          yaw = 0;
          scale = 1 - 0.12 * Math.min(d, 1);
          alpha = clamp(1 - Math.max(0, d - 0.55) / 0.45, 0, 1);
        }
        const layer = 1000 - Math.round(Math.abs(delta) * 100) + (delta < 0 ? 30 : 0);
        setCard(card, y, z, tilt, yaw, scale, alpha, layer);
      });
      if (!reducedMotion.matches && Math.abs(targetPosition - animatedPosition) > 0.002) {
        schedule();
      }
    }
    function schedule() {
      if (!raf) raf = window.requestAnimationFrame(renderDesktop);
    }

    // The original mobile Arc Fan owns its own swipe/arrow animation.
    // These hidden counters let color selections synchronize the image without
    // placing any text over product photography.
    function selectMobileMedia(mediaId) {
      if (desktop.matches || arcCards.length < 2) return;
      const target = mediaIndex(arcCards, mediaId);
      if (target < 0) return;
      const current = Math.max(0, (Number(mobileCurrent?.textContent) || 1) - 1);
      if (current === target) return;
      const forward = (target - current + arcCards.length) % arcCards.length;
      const backward = (current - target + arcCards.length) % arcCards.length;
      const direction = forward <= backward ? 'next' : 'prev';
      const button = arc.querySelector(direction === 'next' ? '[data-ravin-arc-next]' : '[data-ravin-arc-prev]');
      for (let i = 0; i < Math.min(forward, backward); i++) button?.click();
    }

    host.addEventListener('ravin:variantChanged', event => {
      const mediaId = event.detail?.variant?.featured_media?.id || event.detail?.variant?.featured_image?.id;
      if (!mediaId) return;
      if (desktop.matches) {
        const index = mediaIndex(cards, mediaId);
        if (index >= 0) {
          variantOffset = index - scrollPosition();
          schedule();
        }
      } else selectMobileMedia(mediaId);
    }, { signal: controller.signal });

    const mobileObserver = mobileCurrent ? new MutationObserver(() => {
      if (!desktop.matches) host.currentMediaIndex = Math.max(0, (Number(mobileCurrent.textContent) || 1) - 1);
    }) : null;
    if (mobileObserver) mobileObserver.observe(mobileCurrent, { childList: true, subtree:true, characterData:true });

    const startingMedia = root.dataset.initialMediaId;
    if (startingMedia) {
      // Always begin the desktop deck on the first photo; mobile's selected
      // variant photo remains handled by the existing Arc Fan.
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => selectMobileMedia(startingMedia)));
    }

    window.addEventListener('scroll', schedule, { passive:true, signal:controller.signal });
    window.addEventListener('resize', schedule, { passive:true, signal:controller.signal });
    window.addEventListener('pageshow', schedule, { signal:controller.signal });
    desktop.addEventListener?.('change', () => {
      lastActive = -1;
      if (desktop.matches) {
        // Follow the natural scroll position on returning to desktop.
        variantOffset = 0;
        animatedPosition = scrollPosition();
        lastFrameTime = 0;
        schedule();
      } else {
        selectMobileMedia(host.currentVariant?.featured_media?.id || startingMedia);
      }
    }, { signal:controller.signal });
    reducedMotion.addEventListener?.('change', schedule, { signal:controller.signal });

    instances.set(root, () => {
      controller.abort();
      mobileObserver?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
      instances.delete(root);
    });
    schedule();
  }

  function initIn(scope) {
    if (scope.matches?.(selector)) init(scope);
    scope.querySelectorAll?.(selector).forEach(init);
  }
  function unloadIn(scope) {
    const roots = [];
    if (scope.matches?.(selector)) roots.push(scope);
    scope.querySelectorAll?.(selector).forEach(node => roots.push(node));
    roots.forEach(node => instances.get(node)?.());
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initIn(document), { once:true });
  } else initIn(document);
  document.addEventListener('shopify:section:load', event => initIn(event.target));
  document.addEventListener('shopify:section:unload', event => unloadIn(event.target));
})();
